import { CompanyProfileResponse, FileType } from "../../types";

/**
 * Pure completion-math extracted out of the pre-Carbon `CompanyProfileForm.tsx` (Task 7,
 * Profile/Company flow wizard-shell migration). Behaviour is carried over unchanged from the
 * original inline closures — only the shape changed (the profile is now a parameter instead of a
 * captured closure variable, so the functions are callable/testable without mounting the wizard).
 *
 * `calculateProfileCompletion` moves here from `utils/utils.ts` (its only two consumers were
 * `CompanyProfileForm.tsx` and `components/CompletionPercentage/CompletionPercentage.tsx` — both
 * now import it from here instead). `CompletionPercentage.tsx`'s own public API (props, rendered
 * markup) is untouched — it still renders the exact same Tailwind card, just sourcing the one
 * pure number from this module rather than `utils`.
 */

/** A wizard step's completion, 0-100, rounded to the nearest whole percent. */
export const calculateStepCompletion = (
  profile: CompanyProfileResponse | undefined,
  step: number
): number => {
  if (!profile) return 0;

  switch (step) {
    case 1: {
      // Basic Information
      const basicFields = [
        profile.website,
        profile.linkedinUrl,
        profile.annualRevenue,
        profile.taxId,
        profile.commercialName,
        profile.name,
        profile.files.filter((file) => file.type === FileType.PRODUCT_CATALOG)
          .length > 0,
        profile.files.filter(
          (file) => file.type === FileType.COMPANY_PRESENTATION
        ).length > 0,
      ];
      const filledBasicFields = basicFields.filter(
        (field) =>
          field !== null &&
          field !== undefined &&
          field !== "" &&
          field !== 0 &&
          field !== false
      ).length;
      return Math.round((filledBasicFields / basicFields.length) * 100);
    }

    case 2: {
      // Datos de la empresa (Addresses + Banking + Certifications + Tax documents)
      const addresses = profile.addresses || [];
      const bankingInfos = profile.bankingInfos || [];
      const certifications = profile.certifications || [];
      const files = profile.files?.filter(
        (file) => file.type === FileType.TAX_DOCUMENTS
      );

      let completedSections = 0;
      const totalSections = 4;

      if (addresses.length > 0) completedSections++;
      if (bankingInfos.length > 0) completedSections++;
      if (certifications.length > 0) completedSections++;
      if (files.length > 0) completedSections++;

      return Math.round((completedSections / totalSections) * 100);
    }

    case 3: {
      // Contacts
      const contacts = profile.contacts || [];
      return contacts.length > 0 ? 100 : 0;
    }

    case 4: {
      // Main Clients
      const mainClients = profile.mainClients || [];
      return mainClients.length > 0 ? 100 : 0;
    }

    default:
      return 0;
  }
};

/** The whole profile's completion, 0-100, rounded to the nearest whole percent. Moved here
 * unchanged from `utils/utils.ts` — see module doc. */
export const calculateProfileCompletion = (
  profile: CompanyProfileResponse
): number => {
  const fields = [
    profile.website,
    profile.linkedinUrl,
    profile.annualRevenue,
    profile.taxId,
    profile.commercialName,
    profile.name,
    profile.addresses?.length > 0,
    profile.contacts?.length > 0,
    profile.certifications?.length > 0,
    profile.mainClients?.length > 0,
    profile.bankingInfos?.length > 0,
    profile.files?.filter((file) => file.type === FileType.PRODUCT_CATALOG)
      .length > 0,
    profile.files?.filter((file) => file.type === FileType.COMPANY_PRESENTATION)
      .length > 0,
    profile.files?.filter((file) => file.type === FileType.TAX_DOCUMENTS)
      .length > 0,
  ];

  const completedFields = fields.filter(Boolean).length;
  return Math.round((completedFields / fields.length) * 100);
};

export type CompletionLevel = "high" | "medium" | "low";

/** Bands a completion percentage into the same three tiers the pre-Carbon
 * `getBorderColor`/`getBackgroundColor` thresholds used (>=80 / >=50 / below). This is the one
 * place that threshold logic lives now — every visual mapping below derives from it instead of
 * re-testing the percentage itself. */
export const getCompletionLevel = (percentage: number): CompletionLevel => {
  if (percentage >= 80) return "high";
  if (percentage >= 50) return "medium";
  return "low";
};

/** Carbon `ProgressBar`'s `status` prop for a given completion percentage. A profile that is merely
 * incomplete is NOT an error, so this only ever returns `finished` (fully/high complete) or the
 * neutral `active` — never `error`, which rendered an alarming red bar + block icon for a normal
 * low-completion profile. */
export const getCompletionProgressBarStatus = (
  percentage: number
): "finished" | "active" => {
  return getCompletionLevel(percentage) === "high" ? "finished" : "active";
};
